import React, { useEffect, useRef, useState } from "react";
import { Download, FileCode2, ImageOff, Loader2 } from "lucide-react";
import {
  downloadSvgText,
  downloadUrl,
  extensionForMime,
  toSvg,
  toTransparentPng,
} from "../../lib/logoExport";

const FORMATS = [
  {
    key: "original",
    label: "Original",
    hint: "Exactly as generated — no processing",
    icon: ImageOff,
  },
  {
    key: "png",
    label: "PNG",
    hint: "Transparent background",
    icon: Download,
  },
  {
    key: "svg",
    label: "SVG",
    hint: "Vector, transparent background",
    icon: FileCode2,
  },
];

/**
 * One "Download" trigger, one menu, three formats:
 * - Original — the exact bytes Gemini returned, untouched. For comparing
 *   against (or distrusting) the transparency clean-up below.
 * - PNG — the same image with a real transparent background.
 * - SVG — that transparent PNG vectorised into real paths.
 *
 * Used from both the concept grid and the editor, so the format menu and
 * its processing logic only exist in one place.
 */
export default function DownloadMenu({
  dataUrl,
  mimeType,
  filenameBase,
  dense = false,
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null); // one of FORMATS[].key, or null
  const [error, setError] = useState(null);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    function onDocClick(event) {
      if (rootRef.current && !rootRef.current.contains(event.target))
        setOpen(false);
    }
    function onKeyDown(event) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  async function run(key, task) {
    if (busy) return;
    setBusy(key);
    setError(null);
    try {
      await task();
      setOpen(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(null);
    }
  }

  const handlers = {
    original: () =>
      run("original", async () =>
        downloadUrl(
          dataUrl,
          `${filenameBase}-original.${extensionForMime(mimeType)}`,
        ),
      ),
    png: () =>
      run("png", async () => {
        const transparent = await toTransparentPng(dataUrl);
        downloadUrl(transparent, `${filenameBase}.png`);
      }),
    svg: () =>
      run("svg", async () => {
        const transparent = await toTransparentPng(dataUrl);
        const svg = await toSvg(transparent);
        downloadSvgText(svg, `${filenameBase}.svg`);
      }),
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Download"
        className={
          dense
            ? "flex w-full flex-col items-center gap-0.5 rounded py-1.5 text-ink-700 hover:bg-ink-100"
            : "inline-flex h-9 items-center gap-1.5 rounded border border-ink-200 bg-white px-3.5 text-sm font-semibold text-ink-800 hover:border-ink-300 hover:bg-ink-100/60"
        }
      >
        {busy ? (
          <Loader2
            className={
              dense ? "h-3.5 w-3.5 animate-spin" : "h-3.5 w-3.5 animate-spin"
            }
            aria-hidden="true"
          />
        ) : (
          <Download className="h-3.5 w-3.5" aria-hidden="true" />
        )}
        {dense ? (
          <span className="text-[10px] font-semibold uppercase tracking-wide">
            Download
          </span>
        ) : (
          "Download"
        )}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute left-0 top-full z-20 mt-1 w-52 overflow-hidden rounded-soft border border-ink-200 bg-white py-1 shadow-float"
        >
          {FORMATS.map(({ key, label, hint, icon: Icon }) => (
            <button
              key={key}
              type="button"
              role="menuitem"
              onClick={handlers[key]}
              disabled={!!busy}
              className="flex w-full items-center gap-2.5 px-3 py-2 text-left hover:bg-paper disabled:opacity-60"
            >
              {busy === key ? (
                <Loader2
                  className="h-4 w-4 animate-spin text-ink-400"
                  aria-hidden="true"
                />
              ) : (
                <Icon className="h-4 w-4 text-ink-400" aria-hidden="true" />
              )}
              <span className="flex flex-col">
                <span className="text-sm font-medium text-ink-900">
                  {label}
                </span>
                <span className="text-micro text-ink-400">{hint}</span>
              </span>
            </button>
          ))}
        </div>
      )}

      {error && (
        <p className="absolute left-0 top-full z-20 mt-1 w-52 rounded border border-opposed/25 bg-opposed-soft px-2.5 py-1.5 text-micro text-opposed">
          {error}
        </p>
      )}
    </div>
  );
}
